/**
 * @voltxampere/engine — Adaptive step controller (SPECS §3.9)
 *
 * Pure decision function: given the candidate step's local
 * truncation error (LTE), the converged solution's L∞ norm, and the
 * tolerance pair `(reltol, abstol)`, decide whether to accept or
 * reject the step and what `h` to try next.
 *
 * SPECS §3.9 pseudo-code:
 *
 *   threshold = reltol · ||x_corr||_∞ + abstol
 *   if LTE > threshold:
 *     reject, h *= 0.5
 *   else:
 *     accept, advance t
 *     if LTE < threshold / 8:  h *= 1.5
 *
 * Hard step bounds `h_min = 1e-18` and `h_max = tstop/10` are
 * enforced by clamping. Caller is responsible for raising
 * `TransientStepFailedError` if a *rejected* `nextH` would breach
 * `h_min` (the controller still returns the clamped value so the
 * caller can compare against `h_min` itself and surface the right
 * meta).
 *
 * Constants are exposed as module exports (`REJECT_FACTOR`,
 * `GROW_FACTOR`, `GROW_THRESHOLD_RATIO`, `H_MIN_HARD_LIMIT`) so the
 * solver and tests can reference the same values without
 * duplication.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/** SPECS §3.9 step-shrink factor on rejection. */
export const REJECT_FACTOR = 0.5;
/** SPECS §3.9 step-grow factor when LTE is comfortably under threshold. */
export const GROW_FACTOR = 1.5;
/**
 * SPECS §3.9 grow-threshold ratio. Step is grown only when
 * `LTE < threshold * GROW_THRESHOLD_RATIO`.
 */
export const GROW_THRESHOLD_RATIO = 1 / 8;
/** SPECS §3.9 hard floor for the step size — sub-attosecond resolution. */
export const H_MIN_HARD_LIMIT = 1e-18;
/**
 * Build a default constants block with the SPECS §3.9 hard floors.
 *
 * Sprint 6.1 P1-1: before `userTstepMax` existed the solver silently
 * dropped `tstepMax` on the floor, even though the schema accepted
 * and validated it.
 *
 * Sprint 7 P3-3: options-object signature replaces the positional
 * two-arg form. Callers spell the fields explicitly, which reads
 * better at callsites and leaves room for future fields (e.g. an
 * initial-step hint) without breaking the ABI again.
 */
export function defaultAdaptiveStepConstants(options) {
    const { tstop, userTstepMax } = options;
    if (!Number.isFinite(tstop) || tstop <= 0) {
        throw new ContractViolationError(`defaultAdaptiveStepConstants: tstop must be a positive finite number, got ${String(tstop)}`);
    }
    let hMax = tstop / 10;
    if (userTstepMax !== undefined) {
        if (!Number.isFinite(userTstepMax) || userTstepMax <= 0) {
            throw new ContractViolationError(`defaultAdaptiveStepConstants: userTstepMax must be a positive finite number when provided, got ${String(userTstepMax)}`);
        }
        hMax = Math.min(hMax, userTstepMax);
    }
    // Sprint 7 P2: catch degenerate ranges before they reach the
    // controller. `hMax < H_MIN_HARD_LIMIT` means either (a) the user
    // pinned `tstepMax` below the SPECS §3.9 hard floor, or (b) the
    // requested `tstop` is so short that `tstop/10 < 1e-18` (attosecond-
    // scale runs). Both cases make the `[hMin, hMax]` interval empty
    // and leave the step clamps with contradictory demands — the
    // controller cannot make progress. Surface the mis-config loudly
    // here rather than letting the solver spin forever at the boundary.
    if (hMax < H_MIN_HARD_LIMIT) {
        throw new ContractViolationError(`defaultAdaptiveStepConstants: effective hMax (${String(hMax)}) would fall below the SPECS §3.9 hard floor H_MIN_HARD_LIMIT (${String(H_MIN_HARD_LIMIT)}). Check your transient.tstepMax and tstop settings.`);
    }
    return {
        hMin: H_MIN_HARD_LIMIT,
        hMax,
        rejectFactor: REJECT_FACTOR,
        growFactor: GROW_FACTOR,
        growThresholdRatio: GROW_THRESHOLD_RATIO,
    };
}
/**
 * Decide whether to accept the current step and what step size to
 * attempt next. See file-level docstring for the SPECS §3.9
 * algorithm.
 *
 * Sprint 6.1 P1-4: adds an idle-state grow short-circuit. When the
 * entire state vector's infinity norm is below the user's `abstol`
 * (i.e. every node is already below the absolute-tolerance floor),
 * the LTE estimate is dominated by numerical noise rather than
 * integration error — the controller has no basis for distinguishing
 * "true small LTE" from "noise equal to threshold/8". In that
 * regime we allow growth unconditionally so the step marches toward
 * `hMax` on genuinely idle circuits (e.g. a disconnected sub-graph
 * sitting at ground) rather than creeping through `tstop/initialStep`
 * steps. This is defensive hardening against a pathology that
 * doesn't manifest on Sprint 6's linear + diode model set but is a
 * known failure mode once Sprint 7+ ships nanoscale tiny-signal
 * devices.
 */
export function decideAdaptiveStep(input) {
    const { currentH, lte, xNormInf, reltol, abstol, constants } = input;
    if (!Number.isFinite(currentH) || currentH <= 0) {
        throw new ContractViolationError(`decideAdaptiveStep: currentH must be a positive finite number, got ${String(currentH)}`);
    }
    if (!Number.isFinite(lte) || lte < 0) {
        throw new ContractViolationError(`decideAdaptiveStep: lte must be a non-negative finite number, got ${String(lte)}`);
    }
    if (!Number.isFinite(xNormInf) || xNormInf < 0) {
        throw new ContractViolationError(`decideAdaptiveStep: xNormInf must be a non-negative finite number, got ${String(xNormInf)}`);
    }
    const threshold = reltol * xNormInf + abstol;
    if (lte > threshold) {
        const shrunk = currentH * constants.rejectFactor;
        const nextH = Math.max(shrunk, constants.hMin);
        return { action: "reject", nextH, threshold };
    }
    // Grow policy:
    //   - Normal regime: LTE < threshold/8 (SPECS §3.9).
    //   - Idle regime (Sprint 6.1 P1-4): entire state below abstol →
    //     noise-dominated LTE, grow unconditionally.
    const isIdle = xNormInf < abstol;
    const shouldGrow = lte < threshold * constants.growThresholdRatio || isIdle;
    let nextH = currentH;
    if (shouldGrow) {
        const grown = currentH * constants.growFactor;
        nextH = Math.min(grown, constants.hMax);
    }
    return { action: "accept", nextH, threshold };
}
//# sourceMappingURL=adaptive-step.js.map